/**
 * O painel do cliente — spec 04.
 *
 * A navegacao agora e' a **barra de areas** da casca (`web/ui/casca.tsx`), compartilhada com o inbox e
 * o console: um menu por app divergiria, e o owner veria tres navegacoes diferentes no mesmo produto.
 *
 * As telas sao o H24/H25. Cada rota que existir entra aqui; as que nao existem **aparecem na barra**,
 * esmaecidas e com o motivo no `title` — nao escondidas.
 */

import { FaixaDeSuporte, SeletorDeSuporte } from "./Suporte.js";
import { useEffect } from "react";
import { Route, Switch, useLocation } from "wouter";
import { EstadoCarregando, Casca, EstadoSemEntitlement, useSessao } from "@hugi/ui";
import { AindaNaoConstruida } from "./AindaNaoConstruida.js";
import { Canais } from "./Canais.js";
import { NumeroDetalhe, SaudeDoNumero } from "./SaudeDoNumero.js";
import { Conectar } from "./Conectar.js";
import { Ajustes } from "./Ajustes.js";
import { Inicio } from "./Inicio.js";
import { Contatos } from "./Contatos.js";
import { Relatorios } from "./Relatorios.js";
import { Integracoes } from "./Integracoes.js";
import { Historico } from "./Historico.js";
import { Equipe } from "./Equipe.js";
import { Agenda } from "./Agenda.js";
import { Chat } from "./Chat.js";
import { Automacoes } from "./Automacoes.js";
import { CampanhaDetalhe, Campanhas } from "./Campanhas.js";
import { Funil } from "./Funil.js";
import { PublicacaoDetalhe, Publicacoes } from "./Publicacoes.js";
import { GatilhosDeGrupo } from "./GatilhosDeGrupo.js";
import { ListaNegra } from "./ListaNegra.js";
import { PortaDetalhe, PortasDeEntrada } from "./PortasDeEntrada.js";
import { ChamadoDetalhe, Chamados } from "./Chamados.js";

/** As rotas que o painel serve hoje. O resto vive na barra, marcado como pendente. */
const ROTAS: readonly { caminho: string; area: string; rotulo: string }[] = [
  { caminho: "/", area: "inicio", rotulo: "Início" },
  { caminho: "/canais", area: "canais", rotulo: "Canais" },
  // A mesma área da barra, com migalha própria: conectar é um passo DENTRO de Canais, e apagar o item
  // aceso no meio do fluxo faria a barra dizer que a pessoa saiu de onde ela está.
  { caminho: "/canais/conectar", area: "canais", rotulo: "Conectar um canal" },
  // Mesma área, mesmo motivo (spec 19): a saúde é uma pergunta SOBRE o número, e quem a faz continua
  // dentro de Canais. Sem esta linha a migalha dizia "Endereço não encontrado" numa tela que existe.
  { caminho: "/canais/saude", area: "canais", rotulo: "Saúde do número" },
  { caminho: "/contatos", area: "contatos", rotulo: "Contatos" },
  { caminho: "/equipe", area: "equipe", rotulo: "Equipe e acessos" },
  { caminho: "/agenda", area: "agenda", rotulo: "Agenda" },
  { caminho: "/campanhas", area: "campanhas", rotulo: "Campanhas" },
  { caminho: "/funil", area: "funil", rotulo: "Funil de oportunidades" },
  { caminho: "/publicacoes", area: "publicacoes", rotulo: "Grupos" },
  { caminho: "/gatilhos", area: "publicacoes", rotulo: "Gatilhos de grupo" },
  { caminho: "/lista-negra", area: "publicacoes", rotulo: "Lista negra" },
  { caminho: "/portas", area: "publicacoes", rotulo: "Portas de entrada" },
  { caminho: "/chat", area: "chat", rotulo: "Chat da equipe" },
  { caminho: "/automacoes", area: "automacoes", rotulo: "Automações" },
  { caminho: "/automacoes/nova", area: "automacoes", rotulo: "Nova automação" },
  { caminho: "/automacoes/modelos", area: "automacoes", rotulo: "Usar um modelo" },
  { caminho: "/relatorios", area: "relatorios", rotulo: "Relatórios" },
  { caminho: "/historico", area: "historico", rotulo: "Histórico" },
  { caminho: "/integracoes", area: "integracoes", rotulo: "Integrações e API" },
  { caminho: "/chamados", area: "chamados", rotulo: "Chamados" },
  { caminho: "/ajustes", area: "ajustes", rotulo: "Ajustes" },
];

/**
 * As páginas de DETALHE (`/campanhas/<id>`, `/publicacoes/<id>`): tela própria por decisão do owner em
 * 05/09 (*"prefiro que tenha uma tela exclusiva pra isso"*), no lugar do painel ao lado da lista. O id
 * varia, então casam por prefixo; a área da barra é a da lista, para o item continuar aceso.
 */
const DETALHES: readonly { prefixo: string; area: string; rotulo: string }[] = [
  { prefixo: "/canais/saude/", area: "canais", rotulo: "Saúde do número" },
  { prefixo: "/campanhas/", area: "campanhas", rotulo: "Campanha" },
  { prefixo: "/publicacoes/", area: "publicacoes", rotulo: "Publicação" },
  { prefixo: "/portas/", area: "publicacoes", rotulo: "Porta de entrada" },
  { prefixo: "/automacoes/assistente/", area: "assistentes", rotulo: "Assistente" },
  { prefixo: "/automacoes/", area: "automacoes", rotulo: "Automação" },
  { prefixo: "/chamados/", area: "chamados", rotulo: "Chamado" },
];

/**
 * Onde a pessoa cai ao abrir o painel. O Início é a checklist de ativação e lê os canais
 * (`read_channel`); para quem atende e não administra, ele abre TRANCADO — foi a primeira tela que o
 * primeiro atendente convidado viu (03/09). Quem não tem `read_channel` vai direto ao Atendimento.
 * `null` = fica no Início.
 */
export function destinoInicial(permissoes: readonly string[]): string | null {
  return permissoes.includes("read_channel") ? null : "/atender";
}

/** Redireciona dentro da SPA — endereço antigo que passou a ter outro nome. */
function Levar({ para }: { readonly para: string }) {
  const [, navegar] = useLocation();
  useEffect(() => {
    navegar(para, { replace: true });
  }, [navegar, para]);
  return <EstadoCarregando rotulo="Abrindo" />;
}

function IrParaOAtendimento() {
  // O inbox é outro build servido na mesma origem (`/atender`): navegação de documento, não de rota.
  useEffect(() => {
    window.location.replace("/atender");
  }, []);
  return <EstadoCarregando rotulo="Abrindo o atendimento" />;
}

export function Aplicacao() {
  const sessao = useSessao();
  const [caminho] = useLocation();

  // `entitled: false` e' TELA, nao erro (§5 regra 2 da spec 02). O tenant existe e a pessoa existe; o
  // produto nao esta contratado. Um 403 seco aqui perderia a venda.
  // Fora da casca, e por isso com o envelope do fundo: sem ele a tela ficava no branco do navegador.
  if (!sessao.produto.entitled) {
    return (
      <div className="fundo-do-produto grid min-h-screen place-items-center">
        <EstadoSemEntitlement nomeDoTenant={sessao.tenant.nome} />
      </div>
    );
  }

  // Caminho desconhecido **não** cai no Início. Caía, e a casca então mentia junto com a barra: a
  // migalha dizia "Início" e o item ficava aceso enquanto o conteúdo dizia "Endereço não encontrado",
  // duas afirmações contrárias na mesma tela. Área inexistente não casa com nenhum item da barra, que
  // é exatamente o que se quer — nada aceso, e a migalha dizendo o que aconteceu.
  const encontrada = ROTAS.find((r) => r.caminho === caminho) ?? DETALHES.find((d) => caminho.startsWith(d.prefixo) && caminho.length > d.prefixo.length) ?? {
    area: "__sem_area",
    rotulo: "Endereço não encontrado",
  };
  // "Assistentes" é a mesma tela de Automações aberta na lista deles (spec 23): o item aceso segue o `?mostrar=`.
  const atual =
    caminho === "/automacoes" && typeof window !== "undefined" && new URLSearchParams(window.location.search).get("mostrar") === "assistentes"
      ? { area: "assistentes", rotulo: "Assistentes" }
      : encontrada;

  return (
    /*
      ⚠️ A FAIXA VEM ANTES DO CONTEÚDO, e o seletor vai no cabeçalho — as duas coisas, e não uma.

      O seletor é como se entra; a faixa é o que impede de esquecer que se entrou. Só o seletor
      deixaria o operador escolher a conta da cliente e, cinco telas depois, não ter nenhum sinal de
      onde está.
    */
    <Casca area={atual.area} rotuloDaArea={atual.rotulo} acoes={<SeletorDeSuporte />}>
      <FaixaDeSuporte />
      <Switch>
        {/* Antes de `/canais`: o `Switch` para no primeiro que casa, e a ordem inversa esconderia esta. */}
        <Route path="/canais/conectar">
          <Conectar />
        </Route>
        {/* Antes da lista, pelo mesmo motivo do `/canais/conectar`: o `Switch` para no primeiro. */}
        <Route path="/canais/saude/:id">{(p) => <NumeroDetalhe id={String(p.id)} />}</Route>
        <Route path="/canais/saude">
          <SaudeDoNumero />
        </Route>
        <Route path="/canais">
          <Canais />
        </Route>
        <Route path="/contatos">
          <Contatos />
        </Route>
        <Route path="/equipe">
          <Equipe />
        </Route>
        <Route path="/agenda">
          <Agenda />
        </Route>
        <Route path="/campanhas/:id">{(p) => <CampanhaDetalhe id={String(p.id)} />}</Route>
        <Route path="/campanhas">
          <Campanhas />
        </Route>
        <Route path="/funil">
          <Funil />
        </Route>
        <Route path="/portas/:id">{(p) => <PortaDetalhe id={String(p.id)} />}</Route>
        <Route path="/portas">
          <PortasDeEntrada />
        </Route>
        <Route path="/gatilhos">
          <GatilhosDeGrupo />
        </Route>
        <Route path="/lista-negra">
          <ListaNegra />
        </Route>
        <Route path="/publicacoes/:id">{(p) => <PublicacaoDetalhe id={String(p.id)} />}</Route>
        <Route path="/publicacoes">
          <Publicacoes />
        </Route>
        <Route path="/chat">
          <Chat />
        </Route>
        {/*
          AUTOMAÇÕES — a área única (05/09). As rotas específicas vêm ANTES da de id: o `Switch` para
          no primeiro que casa, e `/automacoes/:id` engoliria `/automacoes/nova` se viesse primeiro.
        */}
        <Route path="/automacoes/nova">
          <Automacoes aberto={{ o: "nova" }} />
        </Route>
        <Route path="/automacoes/modelos">
          <Automacoes aberto={{ o: "modelo" }} />
        </Route>
        <Route path="/automacoes/assistente/:id">
          {(p) => <Automacoes aberto={{ o: "assistente", id: String(p.id) }} />}
        </Route>
        <Route path="/automacoes/:id">{(p) => <Automacoes aberto={{ o: "automacao", id: String(p.id) }} />}</Route>
        <Route path="/automacoes">
          <Automacoes aberto={{ o: "lista" }} />
        </Route>
        {/*
          OS ENDEREÇOS ANTIGOS CONTINUAM VALENDO, e isso não é gentileza: `/agente` e `/fluxo` estão
          na ajuda publicada, em atalhos do Início e em links que clientes guardaram. Um 404 ali diria
          que a área foi removida, quando ela foi renomeada.
        */}
        <Route path="/agente">
          <Levar para="/automacoes" />
        </Route>
        <Route path="/fluxo">
          <Levar para="/automacoes" />
        </Route>
        <Route path="/relatorios">
          <Relatorios />
        </Route>
        <Route path="/historico">
          <Historico />
        </Route>
        <Route path="/integracoes">
          <Integracoes />
        </Route>
        {/* O detalhe ANTES da lista: o `Switch` para no primeiro que casa. */}
        <Route path="/chamados/:id">{(p) => <ChamadoDetalhe id={String(p.id)} />}</Route>
        <Route path="/chamados">
          <Chamados />
        </Route>
        <Route path="/ajustes">
          <Ajustes />
        </Route>
        {/*
          `Inicio` PASSOU A EXISTIR: o checklist do §4 da spec 04 depende de saber se ha template
          aprovado e equipe em fila, e as duas coisas ganharam rota — a de template com a sincronizacao,
          e a de equipe pela tabela `fila_atendentes`, que e' o que o Hugi realmente sabe sobre quem
          atende. O passo de convite segue desabilitado com o motivo (H9b).
        */}
        <Route path="/">
          {destinoInicial(sessao.permissoes) === null ? <Inicio /> : <IrParaOAtendimento />}
        </Route>
        <Route>
          <AindaNaoConstruida
            titulo="Endereço não encontrado"
            spec="docs/specs/04-front-painel-do-cliente.md"
            item="H24 / H25"
          />
        </Route>
      </Switch>
    </Casca>
  );
}
